import { withoutInsurancePremium } from '../../supabase/functions/_shared/trip-insurance.js';

/**
 * Pure computation for the Cost Breakdown card on a Flight + Hotel booking.
 *
 * ## Context
 *
 * All Flight + Hotel checkouts land in Review.jsx's `else` branch (L446-458)
 * or its `calculateReviewPagePricing` equivalent, which writes clean,
 * reconciled values to the `bookings` row: `service_fee` waived to $0,
 * `hold_fee` set to `subtotal * 0.15`, and `total_amount = grand_total =
 * grand_total_with_hold` = the number Stripe (or Slice Pay) actually
 * collects across the plan.
 *
 * The display code in FlightDetailsModal *used* to try to recompute all of
 * these from the raw `total_amount` column, but that column already
 * includes the hotel + fees + hold on the Slice Pay / Defera Capital / DZ
 * storage convention. Adding `hotelPrice` on top of it double-counted, and
 * a `hotelPrice > 0` guard in the DZ branch (originally intended to zero
 * out phantom hold fees left over from a change-flight, per 13Q84U) also
 * fired on real flight+hotel bookings and hid the customer's actual
 * charged hold fee entirely. XFZXIZ, July 2026, was the canary: real
 * charged total $3,823.34, displayed as $5,785.98 with the $498.70 hold
 * fee row missing.
 *
 * This helper is the single trust-the-DB path for the Flight + Hotel case.
 * The flight-only self-heals (13Q84U DZ phantom-hold, PY2RE8 stored-column
 * drift, R8BBF5 service-fee-percent, FJO7C0 taxes-and-extras) live in
 * FlightDetailsModal and remain untouched — they run only on the
 * `hotelPrice <= 0` code path.
 *
 * @param {object} args
 * @param {object} args.booking - The booking record (mainFlight / flightWithPrice
 *   shape). Only reads `hold_fee`, `service_fee`, and either
 *   `grand_total_with_hold` or `grand_total`.
 * @param {number} args.hotelPrice - The stay's total, from `stays.total_amount`
 *   (or fallback `nightly_rate * nights`).
 * @param {number} [args.totalPriceFallback] - `flightPrice + hotelPrice` from
 *   the caller — used only if the booking row has no stored grand total.
 * @returns {{
 *   holdFee: number,
 *   serviceFee: number,
 *   grandTotalWithHold: number,
 *   grandTotal: number,
 *   baseFareDisplay: number,
 *   subtotalBeforeHold: number,
 *   flightPortion: number,
 * }}
 *   All values in display dollars (not cents). `baseFareDisplay` is the
 *   pre-fee pre-hold subtotal (flight + hotel), which the Cost Breakdown
 *   renders as the "Subtotal" line. `flightPortion` is that same total
 *   minus the hotel, i.e. the dollars shown against the "Flight" line.
 */
export function computeFlightHotelBreakdown({ booking, hotelPrice, totalPriceFallback = 0 }) {
  // A Faye premium rides inside the stored totals (migration 156); keep it out
  // of the flight and hotel figures. Harmless on an already-stripped row.
  const b = withoutInsurancePremium(booking || {});
  const holdFee = parseFloat(b.hold_fee ?? 0) || 0;
  const serviceFee = parseFloat(b.service_fee ?? 0) || 0;
  const grandTotalWithHold =
    (parseFloat(b.grand_total_with_hold ?? b.grandTotalWithHold ?? b.grand_total ?? b.grandTotal ?? 0) || 0) ||
    totalPriceFallback;
  const grandTotal = grandTotalWithHold - holdFee;
  const baseFareDisplay = Math.max(0, grandTotal - serviceFee);
  const subtotalBeforeHold = baseFareDisplay + serviceFee;
  const flightPortion = Math.max(0, baseFareDisplay - hotelPrice);
  return {
    holdFee,
    serviceFee,
    grandTotalWithHold,
    grandTotal,
    baseFareDisplay,
    subtotalBeforeHold,
    flightPortion,
  };
}
